import { useEffect, useMemo, useRef, useState } from "react";
import { defaultFormConfig, FieldConfig, SectionConfig, FieldType } from "../../formConfig";
import { fetchFormConfigSections, saveFormConfigSections } from "../../api/admin";
import { getUser, isSuperAdmin, ClientPrincipal } from "../../auth/user";

const FIELD_TYPES: FieldType[] = ["text", "textarea", "text_no_input", "date", "email", "radio", "radio_vertical", "checkbox", "checkbox_vertical", "likert", "file", "file_no_input", "person", "currency", "address"];
const FIELD_TYPE_LABELS: Partial<Record<FieldType, string>> = {
  radio_vertical: "radio (vertical)",
  checkbox_vertical: "checkbox (vertical)",
  text_no_input: "Text - No input",
  file_no_input: "File - No input"
};

function blankField(): FieldConfig {
  return { id: `field_${Date.now()}`, question: "New question", type: "text", required: false };
}
function blankSection(): SectionConfig {
  return { title: "New section", shortTitle: "New", fields: [blankField()] };
}

function move<T>(arr: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = arr.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

// Validate and normalise sections before persisting. Throws on invalid input.
function cleanSections(sections: SectionConfig[]): SectionConfig[] {
  const ids = new Set<string>();
  const cleaned: SectionConfig[] = sections.map((s) => ({
    ...s,
    fields: s.fields.map((f) => {
      const out: FieldConfig = { ...f };
      if (out.options) out.options = out.options.map((o) => o.trim()).filter(Boolean);
      if (out.likertRows) out.likertRows = out.likertRows.map((o) => o.trim()).filter(Boolean);
      if (out.likertColumns) out.likertColumns = out.likertColumns.map((o) => o.trim()).filter(Boolean);
      return out;
    })
  }));
  for (const s of cleaned) {
    for (const f of s.fields) {
      if (!f.id.trim()) throw new Error("Every field needs an ID");
      if (ids.has(f.id)) throw new Error(`Duplicate field ID: ${f.id}`);
      ids.add(f.id);
    }
  }
  return cleaned;
}

const AUTOSAVE_DELAY_MS = 800;

export default function FormEditor() {
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [sections, setSections] = useState<SectionConfig[] | null>(null);
  const [activeSection, setActiveSection] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  // Tracks the last successfully persisted payload so we don't re-save unchanged data.
  const lastSavedRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { getUser().then(setUser); }, []);
  useEffect(() => {
    fetchFormConfigSections().then((s) => {
      const initial = s ? JSON.parse(JSON.stringify(s)) : JSON.parse(JSON.stringify(defaultFormConfig));
      lastSavedRef.current = JSON.stringify(initial);
      setSections(initial);
    });
  }, []);

  const isSuper = useMemo(() => isSuperAdmin(user), [user]);

  // Debounced auto-save: persist changes shortly after the user stops editing.
  useEffect(() => {
    if (!sections || !isSuper) return;
    if (JSON.stringify(sections) === lastSavedRef.current) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      let cleaned: SectionConfig[];
      try {
        cleaned = cleanSections(sections);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Save failed");
        return;
      }
      setError(null);
      setSaving(true);
      try {
        await saveFormConfigSections(cleaned);
        lastSavedRef.current = JSON.stringify(sections);
        setSavedAt(new Date().toLocaleTimeString());
      } catch (e) {
        setError(e instanceof Error ? e.message : "Save failed");
      } finally {
        setSaving(false);
      }
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sections, isSuper]);

  if (user && !isSuper) {
    return <div className="banner-error">Super admin required.</div>;
  }
  if (!sections) return <div>Loading…</div>;

  const sec = sections[activeSection];

  const updateSection = (patch: Partial<SectionConfig>) => {
    setSections((arr) => arr!.map((s, i) => (i === activeSection ? { ...s, ...patch } : s)));
  };
  const updateField = (idx: number, patch: Partial<FieldConfig>) => {
    setSections((arr) => arr!.map((s, i) => i === activeSection ? { ...s, fields: s.fields.map((f, j) => j === idx ? { ...f, ...patch } : f) } : s));
  };
  const addField = () => updateSection({ fields: [...sec.fields, blankField()] });
  const removeField = (idx: number) => updateSection({ fields: sec.fields.filter((_, j) => j !== idx) });
  const moveField = (idx: number, dir: -1 | 1) => updateSection({ fields: move(sec.fields, idx, dir) });

  const addSection = () => {
    setSections((arr) => [...arr!, blankSection()]);
    setActiveSection(sections.length);
  };
  const removeSection = () => {
    if (!confirm("Remove this section and all its fields?")) return;
    setSections((arr) => arr!.filter((_, i) => i !== activeSection));
    setActiveSection(Math.max(0, activeSection - 1));
  };
  const moveSection = (dir: -1 | 1) => {
    setSections((arr) => move(arr!, activeSection, dir));
    setActiveSection(Math.max(0, Math.min(sections.length - 1, activeSection + dir)));
  };

  const onResetToDefault = () => {
    if (!confirm("Replace the current form with the original built-in default?")) return;
    setSections(JSON.parse(JSON.stringify(defaultFormConfig)));
    setActiveSection(0);
  };

  return (
    <>
      <div className="admin-page-head">
        <h1>Edit form</h1>
        <a href="/admin" className="btn-secondary">Done</a>
      </div>
      <p className="hint">Edit section titles, field labels, options, and required flags. Adding/removing fields will only affect future submissions; existing answers for removed fields are preserved in storage but no longer displayed.</p>

      <div className="form-editor">
        <aside className="form-editor-aside">
          <h4>Sections</h4>
          <ol>
            {sections.map((s, i) => (
              <li key={i}>
                <button
                  type="button"
                  className={"section-tab " + (i === activeSection ? "active" : "")}
                  onClick={() => setActiveSection(i)}
                >{(s.shortTitle || s.title || "(untitled)") + (s.hidden ? " (hidden)" : "")}</button>
              </li>
            ))}
          </ol>
          <div className="form-editor-section-actions">
            <button type="button" className="btn-secondary" onClick={addSection}>+ Section</button>
            <button type="button" className="btn-secondary" onClick={() => moveSection(-1)} disabled={activeSection === 0}>↑</button>
            <button type="button" className="btn-secondary" onClick={() => moveSection(1)} disabled={activeSection === sections.length - 1}>↓</button>
            <button type="button" className="btn-secondary" onClick={removeSection} disabled={sections.length <= 1}>Remove</button>
          </div>
        </aside>

        <div className="form-editor-main">
          <div className="card">
            <h3 className="card-title">Section settings</h3>
            <div className="form-grid">
              <label>Title<input type="text" value={sec.title} onChange={(e) => updateSection({ title: e.target.value })} /></label>
              <label>Short tab name<input type="text" value={sec.shortTitle} onChange={(e) => updateSection({ shortTitle: e.target.value })} /></label>
            </div>
            <label className="checkbox-inline">
              <input type="checkbox" checked={!!sec.hidden} onChange={(e) => updateSection({ hidden: e.target.checked })} /> Hidden (section is removed from the form and its fields are not required)
            </label>
          </div>

          <div className="card">
            <h3 className="card-title">Fields</h3>
            {sec.fields.map((f, idx) => (
              <FieldEditor
                key={idx}
                field={f}
                onChange={(patch) => updateField(idx, patch)}
                onMoveUp={() => moveField(idx, -1)}
                onMoveDown={() => moveField(idx, 1)}
                onRemove={() => removeField(idx)}
                canMoveUp={idx > 0}
                canMoveDown={idx < sec.fields.length - 1}
              />
            ))}
            <button type="button" className="btn-secondary" onClick={addField}>+ Add field</button>
          </div>

          <div className="actions sticky-actions">
            <button type="button" className="btn-secondary" onClick={onResetToDefault}>Reset to default</button>
            <span className="hint" aria-live="polite">
              {saving ? "Saving…" : savedAt ? `Changes saved automatically at ${savedAt}` : "Changes save automatically"}
            </span>
            {error && <span className="banner-error" style={{ display: "inline-block", marginLeft: ".5rem" }}>{error}</span>}
          </div>
        </div>
      </div>
    </>
  );
}

function FieldEditor({
  field, onChange, onMoveUp, onMoveDown, onRemove, canMoveUp, canMoveDown
}: {
  field: FieldConfig;
  onChange: (patch: Partial<FieldConfig>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const showOptions = field.type === "radio" || field.type === "radio_vertical" || field.type === "checkbox" || field.type === "checkbox_vertical";
  const showLikert = field.type === "likert";
  const showFile = field.type === "file";
  const showStaticText = field.type === "text_no_input";
  const showStaticFile = field.type === "file_no_input";
  const showPerson = field.type === "person";
  const optionsText = (field.options ?? []).join("\n");
  const rowsText = (field.likertRows ?? []).join("\n");
  const colsText = (field.likertColumns ?? []).join("\n");
  return (
    <div className="field-editor">
      <div className="field-editor-row">
        <label className="grow">Question
          <input type="text" value={field.question} onChange={(e) => onChange({ question: e.target.value })} />
        </label>
        <label>Short label
          <input
            type="text"
            value={field.shortLabel ?? ""}
            placeholder="(used for column headers)"
            onChange={(e) => onChange({ shortLabel: e.target.value || undefined })}
            style={{ width: "10rem" }}
          />
        </label>
        <label>ID
          <input type="text" value={field.id} onChange={(e) => onChange({ id: e.target.value.replace(/\s+/g, "") })} style={{ width: "10rem" }} />
        </label>
      </div>
      <div className="field-editor-row">
        <label>Type
          <select value={field.type} onChange={(e) => {
            const type = e.target.value as FieldType;
            onChange({ type, required: type === "text_no_input" || type === "file_no_input" ? false : field.required });
          }}>
            {FIELD_TYPES.map((t) => <option key={t} value={t}>{FIELD_TYPE_LABELS[t] ?? t}</option>)}
          </select>
        </label>
        <label className="grow">Help text (optional)
          <input type="text" value={field.hint ?? ""} onChange={(e) => onChange({ hint: e.target.value || undefined })} />
        </label>
        <label className="checkbox-inline">
          <input type="checkbox" checked={!!field.required} disabled={showStaticText || showStaticFile} onChange={(e) => onChange({ required: e.target.checked })} /> Required
        </label>
      </div>
      {showStaticText && (
        <label>Text shown to respondents
          <textarea
            rows={5}
            value={field.displayText ?? ""}
            placeholder="Enter instructions, guidance, or other read-only form content."
            onChange={(e) => onChange({ displayText: e.target.value, required: false })}
          />
        </label>
      )}
      {showStaticFile && (
        <StaticFileEditor field={field} onChange={onChange} />
      )}
      {showOptions && (
        <label>Options (one per line)
          <textarea
            rows={4}
            value={optionsText}
            onChange={(e) => onChange({ options: e.target.value.split(/\r?\n/) })}
          />
        </label>
      )}
      {showLikert && (
        <div className="field-editor-row">
          <label className="grow">Rows (one per line)
            <textarea rows={5} value={rowsText} onChange={(e) => onChange({ likertRows: e.target.value.split(/\r?\n/) })} />
          </label>
          <label className="grow">Columns (one per line)
            <textarea rows={5} value={colsText} onChange={(e) => onChange({ likertColumns: e.target.value.split(/\r?\n/) })} />
          </label>
        </div>
      )}
      {showFile && (
        <div className="field-editor-row">
          <label>Max files
            <input
              type="number"
              min={1}
              value={field.maxFiles ?? ""}
              placeholder="unlimited"
              onChange={(e) => onChange({ maxFiles: e.target.value ? Number(e.target.value) : undefined })}
              style={{ width: "6rem" }}
            />
          </label>
          <label>Max file size (MB)
            <input
              type="number"
              min={1}
              value={field.maxFileSizeMB ?? ""}
              placeholder="unlimited"
              onChange={(e) => onChange({ maxFileSizeMB: e.target.value ? Number(e.target.value) : undefined })}
              style={{ width: "6rem" }}
            />
          </label>
        </div>
      )}
      {showPerson && (
        <div className="field-editor-row">
          <label>Max people
            <input
              type="number"
              min={1}
              value={field.maxPersons ?? ""}
              placeholder="unlimited"
              onChange={(e) => onChange({ maxPersons: e.target.value ? Number(e.target.value) : undefined })}
              style={{ width: "6rem" }}
            />
          </label>
        </div>
      )}
      <div className="field-editor-actions">
        <button type="button" className="btn-secondary" onClick={onMoveUp} disabled={!canMoveUp}>↑</button>
        <button type="button" className="btn-secondary" onClick={onMoveDown} disabled={!canMoveDown}>↓</button>
        <button type="button" className="btn-secondary" onClick={onRemove}>Remove</button>
      </div>
    </div>
  );
}

function StaticFileEditor({ field, onChange }: { field: FieldConfig; onChange: (patch: Partial<FieldConfig>) => void }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const sasRes = await fetch(`/api/upload-sas?adminAttachment=true&filename=${encodeURIComponent(file.name)}`);
      if (!sasRes.ok) throw new Error("Failed to get upload URL");
      const { uploadUrl, blobUrl } = await sasRes.json();
      let uploaded = false;
      try {
        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type || "application/octet-stream",
            "Content-Length": String(file.size)
          },
          body: file,
          mode: "cors"
        });
        uploaded = putRes.ok;
      } catch {
        /* Fall through to the authenticated proxy. */
      }
      if (uploaded) {
        onChange({ attachment: { name: file.name, url: blobUrl }, required: false });
      } else {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("adminAttachment", "true");
        const proxyRes = await fetch("/api/upload-proxy", { method: "POST", body: formData });
        if (!proxyRes.ok) throw new Error(`Upload failed (${proxyRes.status})`);
        const { blobUrl: proxyBlobUrl } = await proxyRes.json();
        onChange({ attachment: { name: file.name, url: proxyBlobUrl }, required: false });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Upload failed");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <div className="static-file-editor">
      <label>File shown to respondents
        <input type="file" onChange={upload} disabled={uploading} />
      </label>
      {uploading && <span className="hint">Uploading...</span>}
      {field.attachment && (
        <div className="hint">
          Current file: <a href={`/api/download?url=${encodeURIComponent(field.attachment.url)}`} target="_blank" rel="noreferrer">{field.attachment.name}</a>{" "}
          <button type="button" className="btn-link" onClick={() => onChange({ attachment: undefined })}>Remove</button>
        </div>
      )}
      {!field.attachment && !uploading && <span className="hint">Upload the file that respondents should be able to download.</span>}
      {error && <div className="banner-error">{error}</div>}
    </div>
  );
}
